<template>

    <span>
    <Menu/>

    <AppLayout
        title="Dashboard"
        subtitle="Your productivity insights and task analytics"
        :showDefaultControls="false"
    >
        <template #content>
            <div class="dashboard-content">

                <!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- System Health + Velocity side-by-side on lg+ -->
                <b-row class="mb-5">
                    <b-col cols="12" lg="5" class="mb-4 mb-lg-0">
                        <b-card class="shadow-sm h-100" body-class="d-flex flex-column">
                            <template #header>
                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="d-flex align-items-center">
                                        <h4 class="mb-0">
                                            <b-icon icon="shield-check" class="text-primary me-2" />
                                            System Health
                                        </h4>
                                        <b-icon
                                            :icon="healthHelpVisible ? 'x-lg' : 'question-circle'"
                                            style="width:20px;color:gray;cursor:pointer;margin-left:10px;"
                                            @click.stop="healthHelpVisible = !healthHelpVisible"
                                        />
                                    </div>
                                    <div v-if="healthVerdict" style="text-align: right;">
                                        <div style="font-size: 0.65rem; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; margin-bottom: 2px;">Current State</div>
                                        <span style="font-size: 1rem; font-weight: 700;" :style="{ color: healthVerdict.color }">
                                            {{ healthVerdict.word }}
                                        </span>
                                    </div>
                                </div>
                            </template>
                            <div v-show="healthHelpVisible" class="filter-help-panel mb-3">
                                <p class="mb-2"><strong>Verdict (eyebrow)</strong> — proportional: how many of your active tasks currently have a problem vs. how many are in play. Independent of velocity.</p>
                                <ul class="mb-2">
                                    <li><strong>Healthy</strong> — fewer than 15% of active tasks have an issue.</li>
                                    <li><strong>Strained</strong> — 15–40%.</li>
                                    <li><strong>Overloaded</strong> — 40% or more.</li>
                                </ul>
                                <p class="mb-2"><strong>Signals</strong> — what counts as an issue:</p>
                                <ul class="mb-2">
                                    <li><strong>Past due</strong> — incomplete tasks past their due date. Always tracked.</li>
                                    <li><strong>Need a next action</strong> — folders with open work but no task marked Next Action. Tracked once you use that status.</li>
                                    <li><strong>Waiting on others</strong> — open Waiting or Delegated tasks; age is days since last touch. Tracked once you use those statuses.</li>
                                    <li><strong>Someday to review</strong> — Someday tasks with no upcoming remind date. Tracked once you use that status.</li>
                                </ul>
                                <p class="mb-0">Click any signal to see the relevant tasks.</p>
                            </div>
                            <SystemHealth
                                :healthData="healthData"
                                :loading="!healthLoaded"
                                @navigate="handleHealthNavigate"
                            />
                        </b-card>
                    </b-col>
                    <b-col cols="12" lg="7">
                        <VelocityChart />
                    </b-col>
                </b-row>

                <!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- Activity Heatmap (Premium) -->
                <b-card class="mb-5 shadow-sm">
                    <template #header>
                        <div class="d-flex justify-content-between align-items-center">
                            <div class="d-flex align-items-center">
                                <h4 class="mb-0">
                                    <b-icon icon="grid-3x3-gap-fill" class="text-primary me-2" />
                                    Activity
                                    <PremiumTooltip v-if="!isPremiumUser()" />
                                </h4>
                                <b-icon
                                    :icon="activityHelpVisible ? 'x-lg' : 'question-circle'"
                                    style="width:20px;color:gray;cursor:pointer;margin-left:10px;"
                                    @click.stop="activityHelpVisible = !activityHelpVisible"
                                />
                            </div>
                        </div>
                    </template>
                    <div v-show="activityHelpVisible" class="filter-help-panel mb-3">
                        <p class="mb-2">52 weeks of daily task completions — darker cells mean more tasks completed on that day.</p>
                        <ul class="mb-0">
                            <li>Click any cell to view tasks completed on that date.</li>
                            <li>Best week and year-to-date total are shown below the grid.</li>
                            <li>Day-of-week labels (Sun–Sat) are on the left; the color scale runs from light (1–2) to dark (11+).</li>
                        </ul>
                    </div>
                    <ActivityHeatmap
                        :heatmapData="heatmapData"
                        :loading="!heatmapLoaded"
                        :isPremium="isPremiumUser()"
                        @upgrade-clicked="showHeatmapUpgradeModal"
                        @cell-clicked="handleHeatmapCellClick"
                    />
                    <PremiumUpgradeModal
                        ref="heatmapUpgradeModal"
                        featureName="Activity Heatmap"
                        featureDescription="A full year of task completion history at a glance. Each cell represents a day — darker cells mean more tasks completed. Spot your most productive periods and see your best week highlighted."
                        :featureBenefits="['365-day completion heatmap', 'Best week and year-to-date totals', 'Click any day to see what was completed']"
                        crossSell="Plus: Task Velocity, Templates"
                    />
                </b-card>

                <!-- Row: This Week + Folders -->
                <b-row class="mb-5">
                    <b-col cols="12" lg="6" class="mb-4 mb-lg-0">
                        <!-- This Week Chart Card -->
                        <b-card class="shadow-sm h-100">
                            <template #header>
                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="d-flex align-items-center">
                                        <h4 class="mb-0">
                                            <b-icon icon="calendar-week" class="text-primary me-2" />
                                            {{thisWeekHeader}}
                                        </h4>
                                        <b-icon
                                            :icon="thisWeekHelpVisible ? 'x-lg' : 'question-circle'"
                                            style="width:20px;color:gray;cursor:pointer;margin-left:10px;"
                                            @click.stop="thisWeekHelpVisible = !thisWeekHelpVisible"
                                        />
                                        <b-icon icon="gear" style="width:20px;color:gray;cursor:pointer;margin-left:10px;" id="iconThisWeek"/>
                                    </div>
                                </div>
                            </template>
                            <b-popover target="iconThisWeek" triggers="hover" placement="left">
                        <b-form-group
                            id="thisweek-week-group"
                            label="Week"
                            label-for="thisweek-week"
                            description="">

                            <!-- CHANGED (Dec 2024 - Vue 3 migration): Replaced b-form-select with native HTML select -->
                            <!-- WHO: GitHub Copilot (Claude Sonnet 4.5) during chart click handler debugging -->
                            <!-- WHY: BootstrapVue 2.23.1 is Vue 2 only and incompatible with Vue 3.5.26 -->
                            <!-- Using native select ensures Vue 3 compatibility and proper change detection -->
                            <select
                                id="thisweek-week"
                                v-model.number="selectedThisWeek"
                                class="form-control">
                                <option v-for="option in optionsThisWeek" :key="option.item" :value="option.item">
                                    {{ option.name }}
                                </option>
                            </select>
                        </b-form-group>
                        <b-form-group
                            id="thisweek-count-group"
                            label="Count"
                            label-for="thisweek-count"
                            description="">

                            <!-- CHANGED (Dec 2024 - Vue 3 migration): Replaced b-form-select with native HTML select -->
                            <!-- WHO: GitHub Copilot (Claude Sonnet 4.5) during chart click handler debugging -->
                            <!-- WHY: BootstrapVue 2.23.1 is Vue 2 only and incompatible with Vue 3.5.26 -->
                            <!-- Using native select ensures Vue 3 compatibility and proper change detection -->
                            <select
                                id="thisweek-count"
                                v-model="selectedThisWeekCount"
                                class="form-control">
                                <option v-for="option in optionsCount" :key="option.item" :value="option.item">
                                    {{ option.name }}
                                </option>
                            </select>
                        </b-form-group>
                        <b-form-group
                            id="thisweek-completed-group"
                            label="Complete"
                            label-for="thisweek-complete"
                            description="">

                            <!-- CHANGED (Dec 2024 - Vue 3 migration): Replaced b-form-select with native HTML select -->
                            <!-- WHO: GitHub Copilot (Claude Sonnet 4.5) during chart click handler debugging -->
                            <!-- WHY: BootstrapVue 2.23.1 is Vue 2 only and incompatible with Vue 3.5.26 -->
                            <!-- Using native select ensures Vue 3 compatibility and proper change detection -->
                            <select
                                id="thisweek-complete"
                                v-model="selectedThisWeekComplete"
                                class="form-control">
                                <option v-for="option in yesorno" :key="option.item" :value="option.item">
                                    {{ option.name }}
                                </option>
                            </select>
                        </b-form-group>
                    </b-popover>
                    <div v-show="thisWeekHelpVisible" class="filter-help-panel mb-3">
                        <p class="mb-1">Tasks created, started, or due during the selected week. Bars show counts per day of week.</p>
                        <p class="mb-0">Click any bar to see those tasks. Use the gear to change the week or filter by completion status.</p>
                    </div>
                    <div class="chart-container">
                        <GChart
                            v-if="chartDataThisWeekLoaded"
                            type="ColumnChart"
                            ref="chartThisWeek"
                            :data="chartDataThisWeek"
                            :options="chartOptions"
                            :events="eventHandlerThisWeek"
                            @ready="onChartThisWeekReady"
                            :width="1200"
                            :height="400"
                        />
                    </div>
                    <!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- Prior-week comparison line -->
                    <div v-if="weekComparisonText" class="text-muted small mt-2 ps-1">
                        {{ weekComparisonText }}
                    </div>
                        </b-card>
                    </b-col>
                    <b-col cols="12" lg="6">
                        <!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- Folder progress card grid -->
                        <b-card class="shadow-sm h-100">
                            <template #header>
                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="d-flex align-items-center">
                                        <h4 class="mb-0">
                                            <b-icon icon="folder" class="text-success me-2" />
                                            Folders
                                        </h4>
                                        <b-icon
                                            :icon="foldersHelpVisible ? 'x-lg' : 'question-circle'"
                                            style="width:20px;color:gray;cursor:pointer;margin-left:10px;"
                                            @click.stop="foldersHelpVisible = !foldersHelpVisible"
                                        />
                                    </div>
                                </div>
                            </template>
                            <div v-show="foldersHelpVisible" class="filter-help-panel mb-3">
                                <p class="mb-1">One card per folder with open tasks. Progress bar shows completed vs. total. The stall flag (⚠️) appears when no tasks have been completed in 14+ days.</p>
                                <p class="mb-0">Folders with no incomplete tasks are hidden — a finished folder has nothing to stall on.</p>
                            </div>
                            <div class="folder-cards-scroll">
                                <FolderCards
                                    :folders="folderCards"
                                    :loading="!folderCardsLoaded"
                                />
                            </div>
                        </b-card>
                    </b-col>
                </b-row>

                <!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- Breakdown accordion: Context -->
                <b-card class="mb-3 shadow-sm accordion-section">
                    <template #header>
                        <div class="d-flex justify-content-between align-items-center accordion-header" @click="toggleContextAccordion">
                            <h5 class="mb-0">
                                <b-icon icon="tags" class="text-primary me-2" />
                                Context
                            </h5>
                            <div class="d-flex align-items-center">
                                <span v-if="contextSummary" class="text-muted small me-3">{{ contextSummary }}</span>
                                <b-icon :icon="accordionContextOpen ? 'chevron-up' : 'chevron-down'" style="color: #6b7280;" />
                            </div>
                        </div>
                    </template>
                    <div v-show="accordionContextOpen">
                        <b-table
                            :busy="!chartDataContextsLoaded"
                            striped
                            hover
                            small
                            :items="chartDataContexts"
                            :fields="chartDataContextsFields"
                            sort-by="text"
                            sort-icon-right
                            :responsive="true"
                            class="mb-0">

                            <template v-slot:cell(text)="data">
                                <b-link :to="{name: 'tasks-context', params: {context: data.item.text}}">{{data.item.text}}</b-link>&nbsp;
                            </template>

                        </b-table>
                    </div>
                </b-card>

                <!-- 20260606 -- Copilot (Claude Sonnet 4.6) -- Breakdown accordion: Priority/Status -->
                <b-card class="mb-5 shadow-sm accordion-section">
                    <template #header>
                        <div class="d-flex justify-content-between align-items-center accordion-header" @click="togglePSAccordion">
                            <h5 class="mb-0">
                                <b-icon icon="flag" class="text-info me-2" />
                                Priority/Status
                            </h5>
                            <div class="d-flex align-items-center">
                                <span v-if="psSummary" class="text-muted small me-3">{{ psSummary }}</span>
                                <b-icon :icon="accordionPSOpen ? 'chevron-up' : 'chevron-down'" style="color: #6b7280;" />
                            </div>
                        </div>
                    </template>
                    <div v-show="accordionPSOpen">
                        <b-table
                            :busy="!chartDataPSLoaded"
                            striped
                            hover
                            small
                            :items="chartDataPS"
                            :fields="chartDataPSFields"
                            sort-by="prioritystatus"
                            :sort-compare="sortPS"
                            sort-icon-right
                            :responsive="true"
                            class="mb-0">

                            <template v-slot:cell(prioritystatus)="data">
                                <!-- 20260206 -- Copilot (Claude Sonnet 4.5) -- Fixed navigation to use state instead of params -->
                                <!-- WHY: Vue Router 4 doesn't support complex objects in URL params -->
                                <!-- BUG FIX: Was generating /tasks/ps/[object%20Object] -->
                                <span
                                    @click="navigateToPriorityStatus(data.item.priority, data.item.status)"
                                    style="cursor:pointer; color: #007bff; text-decoration: none;"
                                    @mouseover="$event.target.style.textDecoration='underline'"
                                    @mouseout="$event.target.style.textDecoration='none'">

                                    {{data.item.priority}}/{{data.item.status}}

                                </span>
                            </template>

                        </b-table>
                    </div>
                </b-card>

            </div>
        </template>
    </AppLayout>

</span>

</template>

<script>

// 20260606 -- Copilot (Claude Sonnet 4.6) -- Dashboard refresh: System Health, Activity Heatmap, Folder Cards, accordion tables
import AppLayout from '@/components/AppLayout.vue'
import { GChart } from 'vue-google-charts'
import AnalyticsService from '../services/AnalyticsService';
import ActivityHeatmap from '@/components/ActivityHeatmap.vue'
import FolderCards from '@/components/FolderCards.vue'
import Loading from '@/components/Loading.vue'
import Menu from '@/components/menus/UserMenu.vue'
import PremiumTooltip from '@/components/PremiumTooltip.vue'
import PremiumUpgradeModal from '@/components/PremiumUpgradeModal.vue'
import SystemHealth from '@/components/SystemHealth.vue'
import UserService from '@/services/UserService'
import VelocityChart from '@/components/VelocityChart.vue'
import axios from 'axios'
import genericOps from '../mixins/genericOps'
import moment from 'moment';
import taskOps from '../mixins/taskOps'

// 20260606 -- Copilot (Claude Sonnet 4.6) -- Module-level so healthVerdict doesn't recreate on every evaluation
const VERDICT_COLORS = {
    healthy:    { word: 'Healthy',    color: '#71c853' },
    strained:   { word: 'Strained',   color: '#c08010' },
    overloaded: { word: 'Overloaded', color: '#b84040' },
}

export default {
    name: 'UserStats',
    metaInfo() {
			return {
				title: 'Principal Task | Dashboard',
				meta: []
			}
		},
    components: {
        ActivityHeatmap, AppLayout, FolderCards, GChart, Loading, Menu, PremiumTooltip, PremiumUpgradeModal, SystemHealth, VelocityChart
    },
    mixins: [genericOps, taskOps],
    props: {},
    data() {
        return {
            // Context table
            chartDataContexts: [],
            chartDataContextsFields: [
                {key: 'text', label: 'Context', sortable: true, sortDirection:"asc"},
                {key: 'value', label: 'Count', sortable: true}],
            chartDataContextsLoaded: false,
            // Priority/Status table
            chartDataPS: [],
            chartDataPSFields: [
                {key: 'prioritystatus', label: 'Priority/Status', sortable: true, sortDirection:"desc"},
                {key: 'value', label: 'Count', sortable: true}],
            chartDataPSLoaded: false,
            // This Week chart
            // 20260606 -- Copilot (Claude Sonnet 4.6) -- Removed Created column; velocity covers creation tracking
            chartDataThisWeek: [
                ['Day', 'Start', 'Due'],
                ['Monday', 0, 0],
                ['Tuesday', 0, 0],
                ['Wednesday', 0, 0],
                ['Thursday', 0, 0],
                ['Friday', 0, 0],
                ['Saturday', 0, 0],
                ['Sunday', 0, 0],
            ],
            chartDataThisWeekLoaded: false,
            // This week bar chart options
            chartOptions: {
                backgroundColor: 'transparent',
                fontName: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                fontSize: 13,
                legend: {
                    position:'bottom',
                    textStyle: {
                        color: '#1f2937',
                        fontSize: 12
                    }
                },
                chartArea: {
                    left: 60,
                    top: 20,
                    right: 20,
                    bottom: 60,
                    width: '90%',
                    height: '75%'
                },
                hAxis: {
                    textStyle: {
                        color: '#6b7280',
                        fontSize: 11
                    },
                    gridlines: {
                        color: '#e5e7eb',
                        count: 5
                    },
                    minorGridlines: {
                        color: 'transparent'
                    }
                },
                vAxis: {
                    textStyle: {
                        color: '#6b7280',
                        fontSize: 11
                    },
                    gridlines: {
                        color: '#e5e7eb'
                    },
                    minorGridlines: {
                        color: 'transparent'
                    },
                    format: '0'
                },
                // 20260206 -- Copilot (Claude Sonnet 4.5) -- Stacked bar chart
                isStacked: true,
                bar: {
                    groupWidth: '75%'
                },
                // 20260606 -- Copilot (Claude Sonnet 4.6) -- Start (cyan), Due (red); Created removed
                colors: ['#00B4D8', '#ef4444'],
                pieHole: .4,
                sliceVisibilityThreshold: .02,
                tooltip: {
                    textStyle: {
                        fontSize: 12
                    }
                }
            },
            // CHANGED (Dec 2024 - Fix 'this' binding): Event handlers initialized to null, set in created() hook
            // WHO: GitHub Copilot (Claude Sonnet 4.5) during chart click handler debugging
            // WHY: Arrow functions in data() lost 'this' context in Vue 3, causing handlers to fail silently
            eventHandlerThisWeek: null,
            // CHANGED (Dec 2024 - Store chart refs): chartWrapper for click drill-down
            chartWrapperThisWeek: null,
            // Week selector options
            optionsThisWeek: [],
            selectedThisWeek: 0,
            selectedThisWeekComplete: 'A',
            selectedThisWeekCount: 'tasks',
            optionsCount: [],
            // 20260606 -- Copilot (Claude Sonnet 4.6) -- New dashboard panel data
            heatmapData: null,
            heatmapLoaded: false,
            healthData: null,
            healthLoaded: false,
            folderCards: [],
            folderCardsLoaded: false,
            // 20260606 -- Copilot (Claude Sonnet 4.6) -- Prior-week comparison
            // 20260606 -- Copilot (Claude Sonnet 4.6) -- Renamed: chart tracks Start, not Created
            thisWeekStarted: 0,
            prevWeekStarted: null,
            weekComparisonLoaded: false,
            // 20260606 -- Copilot (Claude Sonnet 4.6) -- Accordion state (default collapsed, synced to userPrefs)
            accordionContextOpen: false,
            accordionPSOpen: false,
            // 20260606 -- Copilot (Claude Sonnet 4.6) -- Help panel visibility for System Health, Activity, and Folders
            healthHelpVisible: false,
            activityHelpVisible: false,
            thisWeekHelpVisible: false,
            foldersHelpVisible: false,
        }
    },
    computed: {
        thisWeekHeader: function() {

            let selected = moment(this.selectedThisWeek)

            if(selected.week() == moment().week()) {
                return 'This Week'
            } else {
                return 'Week of ' + selected.format('MM/DD/YYYY')
            }
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Prior-week comparison text
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Updated to use renamed thisWeekStarted/prevWeekStarted
        weekComparisonText() {
            if (!this.weekComparisonLoaded || this.prevWeekStarted === null) return ''
            const diff = this.thisWeekStarted - this.prevWeekStarted
            if (diff === 0) return 'Same number of tasks started as last week'
            if (diff > 0) return `↑ ${diff} more task${diff !== 1 ? 's' : ''} started than last week`
            return `↓ ${Math.abs(diff)} fewer task${Math.abs(diff) !== 1 ? 's' : ''} started than last week`
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Read verdict string from backend (FR-1); map to color client-side (colors are UI-only per FRD §7)
        healthVerdict() {
            const d = this.healthData
            if (!d) return null
            if (d.verdict && d.verdict.word) {
                // 20260607 -- Copilot (Claude Sonnet 4.6) -- Fail safe: unknown verdict word → strained, not healthy
                return VERDICT_COLORS[d.verdict.word] || VERDICT_COLORS.strained
            }
            // Backward-compat fallback for pre-FR-1 server responses that lack verdict.word
            return d.clean ? VERDICT_COLORS.healthy : VERDICT_COLORS.strained
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Accordion summary lines
        contextSummary() {
            if (!this.chartDataContextsLoaded || this.chartDataContexts.length === 0) return ''
            const total = this.chartDataContexts.reduce((sum, c) => sum + c.value, 0)
            return `${this.chartDataContexts.length} context${this.chartDataContexts.length !== 1 ? 's' : ''} · ${total} tasks`
        },
        psSummary() {
            if (!this.chartDataPSLoaded || this.chartDataPS.length === 0) return ''
            const total = this.chartDataPS.reduce((sum, r) => sum + r.value, 0)
            return `${this.chartDataPS.length} combination${this.chartDataPS.length !== 1 ? 's' : ''} · ${total} tasks`
        },
    },
    watch: {
        // CHANGED (Dec 2024 - Native select reactivity): Added watchers to trigger chart data refresh
        // WHO: GitHub Copilot (Claude Sonnet 4.5) during chart click handler debugging
        // WHY: Native HTML select with v-model doesn't auto-call changeWeek() like b-form-select did
        selectedThisWeek() {
            this.changeWeek();
        },
        selectedThisWeekCount() {
            this.changeWeek();
        },
        selectedThisWeekComplete() {
            this.changeWeek();
        },
    },
    async created() {

        const self = this

        // CHANGED (Dec 2024 - Fix event handler binding): Initialize handlers in created() with self pattern
        // WHO: GitHub Copilot (Claude Sonnet 4.5) during chart click handler debugging
        // WHY: Multiple failed approaches - arrow functions lost 'this', computed with .bind() didn't work
        this.eventHandlerThisWeek = {
            select: function() {
                self.handleChartThisWeekSelect()
            }
        }

        // Build week selector options
        let currentWeek = moment()
        let currentWeekNum = currentWeek.week()
        currentWeek.day(1)
        currentWeek.subtract((7*8), 'days')

        for(let i = 0; i < 52; i++) {
            this.optionsThisWeek.push({name: currentWeek.format('MM/DD/YYYY'), item: Number(currentWeek.format('x'))})
            if(currentWeek.week() == currentWeekNum) {
                this.selectedThisWeek = Number(currentWeek.format('x'))
            }
            currentWeek.add(7, 'days')
        }

        this.optionsCount.push({name: 'Tasks', item: 'tasks'})
        this.optionsCount.push({name: 'Length', item: 'length'})

        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Read accordion prefs from userPrefs store
        const prefs = this.$store.getters.getUserPrefs || {}
        this.accordionContextOpen = prefs.dashboardAccordionContext || false
        this.accordionPSOpen = prefs.dashboardAccordionPS || false

        // Load all dashboard data
        this.getDataForFolderCards()
        this.getDataForHeatmap()
        this.getDataForHealth()
        this.getDataForContexts({completed: 'N'})
        this.getDataForPS({completed: 'N'})

        // Note: getDataForWeekChart is triggered by the selectedThisWeek watcher set above

    },
    methods: {
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Show heatmap upgrade modal for non-premium users
        showHeatmapUpgradeModal() {
            this.$refs.heatmapUpgradeModal.show()
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Navigate to completed tasks for a heatmap day cell
        handleHeatmapCellClick(day) {
            // Use UTC day boundaries — backend groups completedDate by UTC midnight
            const utcDayStart = Date.UTC(day.date.year(), day.date.month(), day.date.date())
            const utcDayEnd = utcDayStart + 86400000
            const filter = { filterItems: [] }
            filter.filterItems.push({ group: 'a', field: 'completed', operator: '$eq', value: 'Y' })
            filter.filterItems.push({ group: 'a', field: 'completedDate', operator: '$gte', value: utcDayStart })
            filter.filterItems.push({ group: 'a', field: 'completedDate', operator: '$lt', value: utcDayEnd })
            this.$router.push({ name: 'tasks-system-filter', state: { filter }, query: { _t: Date.now() } })
        },
        // 20260206 -- Copilot (Claude Sonnet 4.5) -- Added navigation method for Priority/Status table clicks
        navigateToPriorityStatus(priority, status) {
            let filter = {filterItems: []}

            let itemP = {
                group: 'a',
                field: 'priority',
                operator: '$eq',
                value: priority
            }
            filter.filterItems.push(itemP)

            let itemS = {
                group: 'a',
                field: 'status',
                operator: '$eq',
                value: status
            }
            filter.filterItems.push(itemS)

            this.$router.push({ name: 'tasks-system-filter', state: { filter: filter }, query: { _t: Date.now() } })
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Navigate from System Health signal to relevant task view
        handleHealthNavigate(signal) {
            let filter = { filterItems: [] }
            const today = moment().startOf('day').format('YYYY-MM-DD')

            if (signal === 'overdue') {
                filter.filterItems.push({ group: 'a', field: 'due', operator: '$lt', value: today })
                filter.filterItems.push({ group: 'a', field: 'completed', operator: '$eq', value: 'N' })
            } else if (signal === 'waitingOnOthers') {
                filter.filterItems.push({ group: 'a', field: 'status', operator: '$eq', value: 'waiting' })
                filter.filterItems.push({ group: 'a', field: 'completed', operator: '$eq', value: 'N' })
                filter.filterItems.push({ group: 'b', field: 'status', operator: '$eq', value: 'delegated' })
                filter.filterItems.push({ group: 'b', field: 'completed', operator: '$eq', value: 'N' })
            } else if (signal === 'somedayToReview') {
                filter.filterItems.push({ group: 'a', field: 'status', operator: '$eq', value: 'someday' })
                filter.filterItems.push({ group: 'a', field: 'completed', operator: '$eq', value: 'N' })
            } else if (signal === 'noNextAction') {
                // 20260606 -- Copilot (Claude Sonnet 4.6) -- Use full allFolders list; Tasks.vue expands 'Unassigned' → folder==null OR folder==''
                const allFolders = this.healthData?.noNextAction?.allFolders || this.healthData?.noNextAction?.folders || []
                const namedFolders = allFolders.filter(f => f !== 'Unassigned')
                const hasOnlyUnassigned = namedFolders.length === 0 && allFolders.includes('Unassigned')
                if (hasOnlyUnassigned) {
                    this.$router.push({ name: 'tasks-folder', params: { folder: 'Unassigned' }, query: { _t: Date.now() } })
                    return
                }
                if (namedFolders.length === 0) {
                    filter.filterItems.push({ group: 'a', field: 'completed', operator: '$eq', value: 'N' })
                } else {
                    namedFolders.forEach((folderName, gIdx) => {
                        const g = String.fromCharCode(97 + gIdx)
                        filter.filterItems.push({ group: g, field: 'folder', operator: '$eq', value: folderName })
                        filter.filterItems.push({ group: g, field: 'completed', operator: '$eq', value: 'N' })
                    })
                }
            }

            this.$router.push({ name: 'tasks-system-filter', state: { filter }, query: { _t: Date.now() } })
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Toggle Context accordion and persist preference
        toggleContextAccordion() {
            this.accordionContextOpen = !this.accordionContextOpen
            this.saveAccordionPrefs()
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Toggle Priority/Status accordion and persist preference
        togglePSAccordion() {
            this.accordionPSOpen = !this.accordionPSOpen
            this.saveAccordionPrefs()
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Save accordion open/closed state to userPrefs (fire-and-forget)
        saveAccordionPrefs() {
            const prefs = { ...(this.$store.getters.getUserPrefs || {}) }
            prefs.dashboardAccordionContext = this.accordionContextOpen
            prefs.dashboardAccordionPS = this.accordionPSOpen
            UserService.updateUserPrefs(prefs, this.$store.getters.getUserToken).catch(() => {
                // Non-critical; silent failure is acceptable for UI preference persistence
            })
        },
        // CHANGED (Dec 2024 - Store chart references): @ready handler captures chart instance for click drill-down
        onChartThisWeekReady(chart, google) {
            this.chartWrapperThisWeek = chart
        },
        // CHANGED (Dec 2024 - Use stored chart refs): Updated select handler to access stored chart
        handleChartThisWeekSelect(chartWrapper) {
            if (!this.chartWrapperThisWeek) return;

            let selection = this.chartWrapperThisWeek.getSelection();
            if (!selection || selection.length === 0) return;

            let selectedDayIndex = selection[0].row
            let selectedCountIndex = selection[0].column

            let weekStart = moment(this.selectedThisWeek)
            weekStart.hour(0)
            weekStart.minute(0)
            weekStart.second(0)
            weekStart.millisecond(0)

            let weekEnd = moment(this.selectedThisWeek)
            weekEnd.hour(0)
            weekEnd.minute(0)
            weekEnd.second(0)
            weekEnd.millisecond(0)

            if(selectedDayIndex != 7) {
                weekStart.add('days', selectedDayIndex)
                weekEnd.add('days', selectedDayIndex + 1)
            } else {
                weekEnd.add(7, 'days')
            }

            let filterColumn = null;

            // 20260606 -- Copilot (Claude Sonnet 4.6) -- Column indices shifted: Start=1, Due=2 (Created removed)
            if(selectedCountIndex == 1) {
                filterColumn = 'start'
            } else if(selectedCountIndex == 2) {
                filterColumn = 'due'
            } else {
                filterColumn = 'xxx'
            }

            let filter = {filterItems: []}

            let itemA = {}
            let itemB = {}
            let itemC = {}

            itemA.group = 'a'
            itemA.field = filterColumn
            itemA.operator = '$gte'
            itemA.value = weekStart.format('YYYY-MM-DD')
            filter.filterItems.push(itemA)

            itemB.group = 'a'
            itemB.field = filterColumn
            itemB.operator = '$lt'
            itemB.value = weekEnd.format('YYYY-MM-DD')
            filter.filterItems.push(itemB)

            if(this.selectedThisWeekComplete != 'A') {
                itemC.group = 'a'
                itemC.field = 'completed'
                itemC.operator = '$eq'
                itemC.value = this.selectedThisWeekComplete
                filter.filterItems.push(itemC)
            }

            // CHANGED (Dec 2024 - Vue Router 4 compatibility): Use state instead of params for complex objects
            // CHANGED (Jan 2026): Use tasks-system-filter to avoid modifying user's custom filter
            this.$router.push({ name: 'tasks-system-filter', state: { filter: filter }, query: { _t: Date.now() } })
        },
        changeWeek() {
            let weekStart = moment(this.selectedThisWeek)
            weekStart.hour(0)
            weekStart.minute(0)
            weekStart.second(0)
            weekStart.millisecond(0)

            let weekEnd = moment(this.selectedThisWeek)
            weekEnd.add(7, 'days')
            weekEnd.hour(0)
            weekEnd.minute(0)
            weekEnd.second(0)
            weekEnd.millisecond(0)

            this.getDataForWeekChart({start: Number(weekStart.format('x')), end: Number(weekEnd.format('x')), completed: this.selectedThisWeekComplete})
        },
        getDataForContexts(filter) {
            this.chartDataContextsLoaded = false
            this.chartDataContexts = []
            let that = this

            AnalyticsService.getDashboardContexts(this.$store.getters.getUser._id, filter, this.$store.getters.getUserToken).then(function (response) {
                let result = response.data.response
                that.chartDataContexts = result.data.map(item => {
                    let container = {};
                    container.text = (typeof item._id.context == 'undefined' || item._id.context === null || item._id.context == '') ? 'Unassigned' : item._id.context
                    container.value = item.count
                    return container;
                })
                that.chartDataContextsLoaded = true
            }).catch(() => { that.chartDataContextsLoaded = true })
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Load folder data for card grid (replaces table)
        getDataForFolderCards() {
            this.folderCardsLoaded = false
            this.folderCards = []
            const that = this

            AnalyticsService.getDashboardFolders(this.$store.getters.getUser._id, {}, this.$store.getters.getUserToken).then(function (response) {
                const result = response.data.response
                that.folderCards = result.data
                    .map(item => ({
                        text: (typeof item._id.folder == 'undefined' || item._id.folder === null || item._id.folder == '') ? 'Unassigned' : item._id.folder,
                        incomplete: item.count || 0,
                        completed: item.completed || 0,
                        total: (item.count || 0) + (item.completed || 0),
                        lastCompleted: item.lastCompleted || null
                    }))
                    // 20260606 -- Copilot (Claude Sonnet 4.6) -- Only show folders with open work; complete folders have nothing to stall on
                    .filter(f => f.incomplete > 0)
                    .sort((a, b) => b.total - a.total)
                that.folderCardsLoaded = true
            }).catch(() => {
                that.folderCardsLoaded = true
            })
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Load heatmap data from new v3 endpoint
        getDataForHeatmap() {
            this.heatmapLoaded = false
            const token = this.$store.getters.getUserToken
            const that = this

            axios.get('/api/v3/dashboard/heatmap', {
                headers: { Authorization: `Bearer ${token}` }
            }).then(response => {
                if (response.data.successful) {
                    that.heatmapData = response.data.data
                }
                that.heatmapLoaded = true
            }).catch(() => {
                that.heatmapLoaded = true
            })
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Load system health data from new v3 endpoint
        getDataForHealth() {
            this.healthLoaded = false
            const token = this.$store.getters.getUserToken
            const that = this

            axios.get('/api/v3/dashboard/health', {
                headers: { Authorization: `Bearer ${token}` }
            }).then(response => {
                if (response.data.successful) {
                    that.healthData = response.data.data
                }
                that.healthLoaded = true
            }).catch(() => {
                that.healthLoaded = true
            })
        },
        getDataForPS(filter) {
            this.chartDataPSLoaded = false
            this.chartDataPS = []
            let that = this

            AnalyticsService.getDashboardPS(this.$store.getters.getUser._id, filter, this.$store.getters.getUserToken).then(function (response) {
                let result = response.data.response
                that.chartDataPS = result.data.map(item => {
                    let container = {};
                    container.priority = (typeof item._id.priority == 'undefined' || item._id.priority === null || item._id.priority == '') ? 'Unassigned' : item._id.priority
                    container.status = (typeof item._id.status == 'undefined' || item._id.status === null || item._id.status == '') ? 'Unassigned' : item._id.status
                    container.value = item.count
                    return container;
                })
                that.chartDataPSLoaded = true
            }).catch(() => { that.chartDataPSLoaded = true })
        },
        getDataForWeekChart(filter) {
            this.chartDataThisWeekLoaded = false
            this.weekComparisonLoaded = false

            // 20260606 -- Copilot (Claude Sonnet 4.6) -- Created column removed; chart now shows Start and Due only
            this.chartDataThisWeek = [
                ['Day', 'Start', 'Due'],
                ['Monday', 0, 0],
                ['Tuesday', 0, 0],
                ['Wednesday', 0, 0],
                ['Thursday', 0, 0],
                ['Friday', 0, 0],
                ['Saturday', 0, 0],
                ['Sunday', 0, 0],
            ]

            let that = this
            filter.count = this.selectedThisWeekCount

            AnalyticsService.getDashboardThisWeek(this.$store.getters.getUser._id, filter, this.$store.getters.getUserToken).then(function (response) {
                let result = response.data.response

                // 20260606 -- Copilot (Claude Sonnet 4.6) -- Start is now index 1, Due is index 2 (Created removed)
                result.data.week.start.forEach((element) => {
                    let dayIndex = Number(moment(element._id).format('d'))
                    if(dayIndex == 0) { dayIndex = 7 }
                    that.chartDataThisWeek[dayIndex][1] += element.count
                })

                // 20260615 -- Copilot (Claude Sonnet 4.6) -- Fix: _id IS the timestamp; .due was undefined, collapsing all dues to today
                // TODO(Copilot): Add Vitest test for this bucketing logic — extract a pure mapWeekElements(elements)
                // function from this block and assert that a Wednesday timestamp lands in chartDataThisWeek[3][2].
                // API-level contract (_id is a number, not an object) is already covered in
                // cypress/support/api-helpers/v3/dashboard.js validateGetDashboardThisweekWithFilters().
                result.data.week.due.forEach((element) => {
                    let dayIndex = Number(moment(element._id).format('d'))
                    if(dayIndex == 0) { dayIndex = 7 }
                    that.chartDataThisWeek[dayIndex][2] += element.count
                })

                // 20260606 -- Copilot (Claude Sonnet 4.6) -- Sum started tasks for comparison line
                // 20260606 -- Copilot (Claude Sonnet 4.6) -- Fixed: was summing week.created; chart tracks Start so comparison must also use week.start
                let started = 0
                result.data.week.start.forEach((element) => { started += element.count })
                that.thisWeekStarted = started
                that.getDataForWeekComparison()

                // 20260210 -- Copilot (Claude Sonnet 4.5) -- Use $nextTick to ensure DOM is ready before chart renders
                that.$nextTick(() => {
                    that.chartDataThisWeekLoaded = true
                })
            }).catch(() => {
                that.$nextTick(() => {
                    that.chartDataThisWeekLoaded = true
                })
            })
        },
        // 20260606 -- Copilot (Claude Sonnet 4.6) -- Load prior week data for comparison line
        getDataForWeekComparison() {
            const prevWeekStart = moment(this.selectedThisWeek).subtract(7, 'days')
            const prevWeekEnd = prevWeekStart.clone().add(7, 'days')
            const filter = {
                start: Number(prevWeekStart.format('x')),
                end: Number(prevWeekEnd.format('x')),
                completed: 'A',
                count: 'tasks'
            }
            const that = this

            // 20260606 -- Copilot (Claude Sonnet 4.6) -- Use week.start to match current-week metric (was week.created, which diverged when chart switched from Created to Start)
            AnalyticsService.getDashboardThisWeek(this.$store.getters.getUser._id, filter, this.$store.getters.getUserToken).then(response => {
                const result = response.data.response
                // 20260606 -- Copilot (Claude Sonnet 4.6) -- Updated to use renamed prevWeekStarted
                let prevStarted = 0
                result.data.week.start.forEach(el => { prevStarted += el.count })
                that.prevWeekStarted = prevStarted
                that.weekComparisonLoaded = true
            }).catch(() => {
                that.weekComparisonLoaded = false
            })
        },
        sortPS(aRow, bRow, key, sortDesc, formatter, compareOptions, compareLocale) {

            let rc = 0

            let aPriority = this.validPriorityRanking[aRow['priority'].toLowerCase()]
            let aStatus = aRow['status'].toLowerCase()

            let bPriority = this.validPriorityRanking[bRow['priority'].toLowerCase()]
            let bStatus = bRow['status'].toLowerCase()

            if(aPriority > bPriority) {
                rc = 1
            } else if(aPriority < bPriority) {
                rc = -1
            } else if (aPriority == bPriority) {
                if(aStatus > bStatus) {
                    rc = 1
                } else if(aStatus < bStatus) {
                    rc = -1
                } else if (aStatus == bStatus) {
                    rc = 0
                }
            }

            return rc
        },

    }
}
</script>

<style scoped>
.dashboard-content {
    max-width: 1400px;
    margin: 0 auto;
}

.card {
    border-radius: 8px;
    border: 1px solid #e0e0e0;
}

.card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #e0e0e0;
    padding: 1rem 1.25rem;
}

.shadow-sm {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}

/* Ensure tables in cards have proper spacing */
.card .table {
    margin-bottom: 0;
}

/* Icon spacing */
.me-2 {
    margin-right: 0.5rem;
}

/* 20260606 -- Copilot (Claude Sonnet 4.6) -- Help panels match Tasks.vue filter-help-panel style exactly */
.filter-help-panel {
    background-color: #e9ecef;
    border-radius: 0.25rem;
    padding: 0.75rem 1rem;
    font-size: 0.85rem;
    color: #495057;
}

.filter-help-panel ul {
    padding-left: 1.25rem;
    margin-bottom: 0;
}

/* 20260606 -- Copilot (Claude Sonnet 4.6) -- Accordion header is clickable */
.accordion-header {
    cursor: pointer;
    user-select: none;
}

/* 20260606 -- Copilot (Claude Sonnet 4.6) -- Folder cards scrollable within card body */
.folder-cards-scroll {
    max-height: 480px;
    overflow-y: auto;
}
</style>
